// FP 간트(자체 구현) — 프레임: 레이아웃 · 스크롤 · 행 가상화 · 2단 시간 헤더 · 입력 창구
//
// **AG Grid를 쓰지 않는다.** 겹침 수직 스택(행 높이 가변)·휠 줌·드래그 편집 같은 요구가 그리드
// 프레임 안에서 급격히 비싸지기 때문이다(결정 근거: reports/gantt-implementation.md §7-1).
// 대신 그리드가 공짜로 주던 것(가상화·스크롤·좌측 컬럼·키보드)을 여기서 직접 만든다.
//
// 레이어 구성 — 입력 창구를 **하나**로 유지하는 게 핵심이다:
//   [헤더]            스크롤 밖. scrollLeft를 transform으로 따라간다
//   [좌측 pane]       DOM 렌더 전용(pointerEvents:none), scrollTop을 transform으로 따라간다
//   [타임라인 캔버스]  뷰포트 크기. 스크롤 오프셋을 좌표에 반영해 그린다
//   [스크롤 레이어]    투명 + 최상단. **스크롤·클릭·키보드를 전부 여기서 받는다**
// 이렇게 하면 "그리드가 그린 것을 외부에서 다시 좌표계산" 하는 문제(B4·B10·B13~B15)가 원천적으로
// 없다 — 우리가 그린 좌표를 우리가 그대로 쓴다.

import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";

import GanttLeftPane, { LEFT_PANE_CHEVRON_WIDTH, treeIndentWidth } from "@plannel/pages/factory-plan/common/gantt/GanttLeftPane";
import { findRowIndexAtY, visibleRowRange } from "@plannel/pages/factory-plan/common/gantt/ganttRowModel";
import { visibleTickRange } from "@plannel/pages/factory-plan/common/gantt/timelineScales";
import {
  TIMELINE_HEADER_HEIGHT,
  TIMELINE_HEADER_TOP_HEIGHT,
  UI
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

// 색은 전부 `UI` 토큰(테마 "FP Production", ganttConstants.js)에서 온다 — 여기에 색을 직접 쓰지 말 것.
const COL_MIN_WIDTH = 60;         // 드래그로 줄일 수 있는 하한(px)
// 가로 스크롤바 — **전용 얇은 스크롤 영역의 네이티브 스크롤바를 쓴다.**
// 왜 이 구조인가:
//  ① 메인 스크롤러(입력 레이어)의 네이티브 가로 스크롤바는 이 레이어 구성에서 thumb 드래그가
//     동작하지 않았다(3라운드 조사, 원인 미특정 — §7-2k).
//  ② 그래서 직접 그려봤지만 트랙·thumb·화살표 색을 값으로 추정하는 방식은 세로(네이티브)와 계속
//     미묘하게 달랐다. 스크롤바는 OS·브라우저·테마마다 모양이 달라 하드코딩으로는 맞출 수 없다.
//  → 가로도 **네이티브 스크롤바 그 자체**를 쓰면 세로와 100% 같은 부품이 된다. AG Grid가 쓰는 기법
//    (`.ag-body-horizontal-scroll-viewport` = 실제 스크롤 div)과 같다.
// 스크롤 위치의 소유권은 그대로 우리에게 있다 — 이 영역의 `scroll` 이벤트를 받아 반영하고,
// 휠·패닝으로 움직일 때는 반대로 이 영역의 `scrollLeft`를 맞춰준다.
const H_BAR_FALLBACK = 15;        // 두께 실측 전 폴백
// 헤더 눈금을 뷰포트 밖으로 더 렌더할 최소 폭(px). 실제로는 `max(이 값, 뷰포트 폭)` 을 쓴다.
const TICK_BUFFER_MIN = 400;

/**
 * 시간 헤더의 **눈금 칸 목록**. `ticks` 가 그대로면 재렌더를 건너뛴다(`memo`).
 *
 * ⚠️ **분리한 이유는 성능이다**(2026-09-10, §7-34). 눈금은 축 전체를 DOM 으로 렌더하는데,
 * Hour 버킷 + 1년 계획이면 **하단 8,760 + 상단 366 = 9,126개**의 MUI `sx` Box 다. `sx` 는 렌더마다
 * emotion 직렬화를 거치므로(`serializeStyles`/`handleInterpolation`/`getThemeValue`), 이게
 * `GanttFrame` 안에 인라인으로 있으면 **부모가 재렌더될 때마다 9,126번** 다시 돈다.
 *
 * 실측(자원 60 · 작업 37,000, Hour 버킷): hover 한 번에 프레임이 **250~1,184ms**. Chrome
 * 프로파일에서 Scripting 4,455ms 중 emotion 계열이 약 49%였고 **Rendering 44ms · Painting 17ms** 로
 * DOM·페인트는 무죄였다 — 즉 캔버스 드로잉이 아니라 **`sx` 직렬화**가 원인이었다.
 * `ticks` 는 `[scale, weekdayLabels]` 에만 의존하므로(호출부 memo) hover·선택·편집에 안 바뀐다.
 *
 * **가시 구간만 렌더한다**(§7-35). `range` 는 `syncScroll` 이 `visibleTickRange` 로 계산해 **범위가
 * 바뀔 때만** 갱신하므로 스크롤 픽셀마다 재렌더되지 않는다(세로축 `visibleRowRange` → `setRange` 와
 * 같은 구조다). `ticks` 배열은 전체를 그대로 받아 **인덱스로 잘라낸다** — `buildTicks` 는 13ms 라
 * 범위마다 다시 만드는 게 오히려 손해다.
 *
 * ⚠️ 잘린 칸도 `tick.left` **절대 좌표를 그대로** 쓴다. 컨테이너가 `width: timelineWidth` 인 채로
 * `translateX(-scrollLeft)` 되므로 좌표를 다시 계산하면 안 된다.
 *
 * ⚠️ **`Box`/`sx` 가 아니라 순수 `div`/`style` 이다**(§7-36). C안(가시 구간만 렌더)으로 개수를
 * 9,126 → ~160 으로 줄인 뒤에도 **스크롤로 범위가 바뀔 때마다 그 160개가 언마운트/마운트**되어
 * 걸림이 남았다(칸당 emotion 약 0.27ms × 160 ≈ 43ms — 한 프레임 16ms 초과). 개수(C)와 단가(B)는
 * **곱해진다** — 둘 다 필요하다.
 *
 * `sx` 를 쓸 이유가 애초에 없었다: 여기 쓰이는 값은 전부 평범한 CSS 리터럴이고
 * (`UI.headerFg` 등) **MUI 테마 조회(`palette.*`/`spacing()`/breakpoints)가 하나도 없다.**
 * `Box` 자체도 styled 컴포넌트라 `sx` 없이도 emotion 을 거치므로 **`div` 로 바꿔야** 효과가 있다.
 *
 * ⚠️ 옮길 때 실수하기 쉬운 두 가지:
 *   ① `bgcolor` 는 **MUI 단축이라 `style` 에서 안 먹는다** → `backgroundColor`
 *   ② `boxSizing: "border-box"` **필수** — 없으면 칸 폭이 `width + 1px(borderRight)` 가 되어
 *      다음 칸이 앞 칸의 테두리 픽셀을 덮는다. 주말 칸처럼 배경색이 있으면 그 배경이 테두리를
 *      지운다("토요일 시작 경계가 안 그려진다", 2026-08-21 신고). 아래 상수에 들어 있다.
 */
/**
 * 눈금 칸의 **정적 스타일**. 모듈 상수로 뽑아 칸마다 다시 만들지 않는다
 * (`1px solid …` 템플릿 문자열도 여기서 한 번만 만들어진다).
 */
const TICK_STYLE_BASE = {
  position: "absolute",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: UI.headerFontSize,
  fontWeight: UI.headerFontWeight,
  overflow: "hidden",
  whiteSpace: "nowrap"
};
const TICK_STYLE_TOP = { ...TICK_STYLE_BASE, top: 0, height: TIMELINE_HEADER_TOP_HEIGHT, borderRight: `1px solid ${UI.paneSep}` };
const TICK_STYLE_BOTTOM = { ...TICK_STYLE_BASE, top: TIMELINE_HEADER_TOP_HEIGHT, bottom: 0, borderTop: `1px solid ${UI.colLine}`, borderRight: `1px solid ${UI.colLine}` };
const TICK_MAJOR_BORDER = `1px solid ${UI.paneSep}`;

const TimelineHeaderTicks = memo(({ ticks, range }) => (
  <>
    {ticks.top.slice(range.topFirst, range.topLast + 1).map(tick => (
      <div key={`t-${tick.key}`} style={{ ...TICK_STYLE_TOP, left: tick.left, width: tick.width, color: UI.headerFg }}>
        {tick.label}
      </div>
    ))}
    {ticks.bottom.slice(range.botFirst, range.botLast + 1).map(tick => (
      <div
        key={`b-${tick.key}`}
        style={{
          ...TICK_STYLE_BOTTOM,
          left: tick.left,
          width: tick.width,
          color: tick.weekend ? UI.headerFgDim : UI.headerFg,
          borderLeft: tick.major ? TICK_MAJOR_BORDER : "none",
          backgroundColor: tick.weekend ? UI.weekendHeaderBg : "transparent"
        }}
      >
        {tick.label}
      </div>
    ))}
  </>
));
// 비보안 컨텍스트(http)용 복사 폴백. `document.execCommand`는 폐지 예정이지만 **동기 복사의 유일한
// 대안**이라 Clipboard API가 없을 때만 쓴다.
const legacyCopy = (text) => {
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  area.style.position = "fixed";
  area.style.opacity = "0";
  document.body.appendChild(area);
  area.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(area);
  }
};

// `navigator.clipboard`는 **보안 컨텍스트(https / localhost)에만 존재한다.** 사내망 http 주소로 접속하면
// undefined라 조용히 아무 일도 일어나지 않으므로 폴백을 둔다.
const writeClipboard = (text) => {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
    return;
  }
  legacyCopy(text);
};

const GanttFrame = ({
  rows = [],                 // buildRowLayout 결과(top/height 포함)
  totalHeight = 0,
  columns = [],              // 좌측 컬럼 정의
  scale,                     // buildTimeScale 결과 | null
  ticks = { top: [], bottom: [] },
  expandedKeys,              // Set<rowKey> | undefined (트리 화면만)
  selectedRowKey,
  loading = false,           // 조회 중 — 프레임 안에 진행 바 + 옅은 베일 + 안내 카드
  loadingMessage = "",       // 화면이 번역한 문구(공통 키 `MSG_Loading`)를 넘긴다
  drawBackground,            // (ctx, info) => void — 프로즌존 등 전 행 관통 배경
  drawRow,                   // (ctx, info) => void — 행 본문(바·다운타임…)
  drawOverlay,               // (ctx, info) => void — 의존선 등 행 위 레이어
  onToggleRow,               // (row) => void
  onRowActivate,             // (row, { ts, clientX, clientY }) => void — 클릭
  onRowDoubleActivate,       // (row, { ts, clientX, clientY }) => void
  onHoverPoint,              // (row | null, { ts, clientX, clientY, inLeftPane, colKey }) => void
                             //   좌측 pane 위에서도 호출된다(ts=null, inLeftPane=true, colKey=그 컬럼)
  onWheelZoom,               // (delta, { ts }) => void — Ctrl+휠
  onContextPoint,            // (row, { ts, clientX, clientY }) => void — 타임라인 우클릭(컨텍스트 메뉴)
  // 컬럼 값 필터 — `column.filterable`인 헤더 셀에 깔때기 아이콘을 띄우고 클릭을 올려준다.
  // 팝오버는 **화면이** 띄운다(값 목록·필터 상태가 도메인이라 프레임이 알 필요가 없다).
  onColumnFilterClick,       // (column, anchorEl) => void
  filterTitle = "",          // 아이콘 tooltip. `loadingMessage`와 같은 규칙 — 화면이 번역해서 넘긴다
  // ── 편집 드래그 (AdjustmentGantt에서만 전달, 미전달 시 동작 불변) ──────────────
  // 프레임은 **박스를 모른다.** 좌표→행·시각까지만 되돌려 주고 "무엇을 잡았는가/놓을 수 있는가"는
  // 전부 화면이 판정한다(좌표 소유권 규약 — `onContextPoint`와 같은 계약).
  // `button`은 0(좌) 또는 2(우)다 — 우클릭 드래그를 별개 제스처로 쓰는 화면이 있다(old
  // `GanttRunMoveRight:'Slide'` = 같은 행 안에서만 수평 이동). 어느 버튼에 무슨 뜻을 줄지는 화면이 정한다.
  onBoxDragStart,            // (row, { ts, clientX, clientY, button }) => boolean — true면 드래그 시작
  // 우클릭을 제스처로 쓰는 화면은 브라우저 기본 메뉴를 막아야 한다(안 그러면 드래그를 놓는 순간 메뉴가 뜬다).
  suppressContextMenu = false,
  onBoxDragMove,             // (row | null, { ts, axisTs, clientX, clientY, inLeftPane }) => void
                             //   `ts`는 좌측 pane 위에서 null, `axisTs`는 pane 무시하고 늘 계산된다
  onBoxDragEnd,              // (row | null, { ts, clientX, clientY, moved }) => void
  onBoxDragCancel,           // () => void — Esc / 창 밖에서 취소
  resolveCursor,             // (row | null, { ts, inLeftPane }) => string | null — hover 커서 모양
  apiRef
}) => {
  const outerRef = useRef(null);
  const scrollerRef = useRef(null);
  const headerInnerRef = useRef(null);
  const leftInnerRef = useRef(null);
  const canvasRef = useRef(null);
  const hScrollRef = useRef(null);
  const rafRef = useRef(0);
  const scrollRef = useRef({ left: 0, top: 0 });
  const viewportRef = useRef({ width: 0, height: 0 });

  const [range, setRange] = useState({ first: 0, last: -1 });
  const [focus, setFocus] = useState({ rowKey: null, colKey: null });
  // 펼침 표식 hover — 좌측 pane은 `pointerEvents:none`이라 CSS `:hover`가 발동하지 않는다. 입력을
  // 소유한 이 레이어가 좌표로 판정해 내려준다. **값이 바뀔 때만** set하므로 스크롤·이동마다
  // 리렌더가 걸리지는 않는다(행이 바뀌는 순간에만 1회).
  const [chevronHoverKey, setChevronHoverKey] = useState(null);
  // 네이티브 세로 스크롤바 두께(실측). 자체 가로 스크롤바가 같은 두께를 쓴다.
  const [barSize, setBarSize] = useState(H_BAR_FALLBACK);

  /**
   * **세로 스크롤바에 내주는 오른쪽 여백**(px). 스크롤바가 실제로 생길 때만 `barSize`, 아니면 0.
   *
   * 이게 없던 동안 **캔버스가 스크롤바 아래까지 그려졌다.** 폭 계산이
   * `rect.width - leftWidth` 라 스크롤바 두께를 빼지 않았고, 같은 값이 `setScrollLeft` 의 상한
   * (`timelineWidth - viewport.width`)에도 쓰여서 **축 마지막 ~두께만큼은 스크롤해도 드러나지
   * 않았다** — 계획 구간 끝의 작업이 영구히 가려졌다(2026-09-10 신고).
   * 높이는 처음부터 자체 가로 바 몫(`barSize`)을 빼고 있었으니 **폭만 빠져 있던 비대칭**이다.
   *
   * ⚠️ **세 곳이 한 세트다** — 하나만 고치면 조용히 반쪽만 고쳐진다:
   *   ① `viewportRef.current.width` 차감      → 캔버스 폭 + 스크롤 상한
   *   ② 가로 스크롤바 `right`                 → **자기 스크롤 범위**가 캔버스와 같아야 한다.
   *      안 맞추면 상한만 커지고 **그 값에 도달할 입력 수단이 없다.**
   *   ③ 헤더 눈금 clip 뒤 스페이서            → 헤더가 캔버스보다 넓게 보이지 않게.
   *      헤더 밴드 자체는 `right: 0` 을 유지해야 한다(안 그러면 헤더 띠가 끊긴다).
   *
   * **고정이 아니라 판정이다** — 행이 적어 스크롤바가 없으면 그 자리를 캔버스가 쓴다.
   * 순환은 없다: 예약은 **폭**만 바꾸고 넘침 판정은 **높이**로 하며, 뷰포트 높이는 가로 바 몫만
   * 빼므로 예약 폭에 의존하지 않는다. 대가는 행 수가 뷰포트 경계를 오갈 때(트리 펼침·자원 필터·
   * 세로 줌) 타임라인 폭이 `barSize` 만큼 한 번 바뀌는 것이다.
   */
  const [vGutter, setVGutter] = useState(0);

  /**
   * **보이는 헤더 눈금 범위** — 가로축 가상화(§7-35). 세로 `range`(행)의 거울상이다.
   * `syncScroll` 이 갱신하고 `TimelineHeaderTicks` 가 이걸로 잘라 렌더한다.
   * 초기값이 빈 범위(`last: -1`)인 건 마운트 직후 `measure()` → `syncScroll()` 이 곧바로 채우기 때문이다.
   */
  const [tickRange, setTickRange] = useState({ topFirst: 0, topLast: -1, botFirst: 0, botLast: -1 });

  // 사용자가 드래그로 조절한 컬럼 폭(일반 그리드와 같은 조작). 화면이 넘긴 정의를 덮어쓴다.
  const [widthOverrides, setWidthOverrides] = useState({});
  const resizeRef = useRef(null);

  // 실제 렌더·좌표 계산에 쓰는 컬럼. **이 배열만 쓴다** — 원본 `columns`를 섞어 쓰면 폭을 조절한
  // 뒤 히트 판정(트리 ▶)·병합 셀 위치가 어긋난다.
  const cols = useMemo(
    () => columns.map(column => ({ ...column, width: widthOverrides[column.key] ?? column.width })),
    [columns, widthOverrides]
  );

  const leftWidth = useMemo(
    () => cols.reduce((sum, column) => sum + column.width, 0),
    [cols]
  );

  // 컬럼 왼쪽 시작 x(px) — 헤더 리사이즈 핸들, 병합 셀, 트리 ▶ 히트 판정이 공유한다.
  const colOffsets = useMemo(() => {
    let offset = 0;
    return cols.map(column => {
      const left = offset;
      offset += column.width;
      return left;
    });
  }, [cols]);

  // 같은 값이 연속되는 구간을 하나로 합친 셀(old TreeGrid `Spanned:'1'`, AG Grid `spanRows`).
  // **전체 행**을 기준으로 계산한다 — 보이는 슬라이스만 보면 뷰포트 위에서 시작한 병합 구간을
  // 놓쳐서 스크롤할 때마다 병합이 끊긴다.
  const spanGroups = useMemo(() => {
    const groups = [];

    cols.forEach((column, colIndex) => {
      if (!column.spanRows || rows.length === 0) return;

      let i = 0;
      while (i < rows.length) {
        const value = column.valueGetter(rows[i]);
        let j = i;
        while (j + 1 < rows.length && column.valueGetter(rows[j + 1]) === value) j += 1;

        groups.push({
          key: `${column.key}:${rows[i].rowKey}`,
          column,
          left: colOffsets[colIndex],
          top: rows[i].top,
          height: rows[j].top + rows[j].height - rows[i].top,
          value,
          row: rows[i],
          first: i,
          last: j
        });
        i = j + 1;
      }
    });

    return groups;
  }, [cols, colOffsets, rows]);

  const spannedKeys = useMemo(
    () => new Set(cols.filter(column => column.spanRows).map(column => column.key)),
    [cols]
  );

  const timelineWidth = scale?.width ?? 0;
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // ── 그리기 ────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // **CSS 크기를 정수로 고정한다.** 뷰포트 크기는 `getBoundingClientRect()`에서 와서 소수다
    // (예: 1543.6px). 그 값을 그대로 `style.width`에 넣으면 비트맵(= round(폭 × dpr))과 CSS 박스의
    // 비율이 dpr과 미세하게 어긋나고, 브라우저가 캔버스를 **재스케일**한다 → 바·글자가 전부 약간
    // 흐려진다(2026-08-19 사용자 지적). 변환도 dpr이 아니라 **실제 비율**로 걸어 반올림 오차를 없앤다.
    const cssWidth = Math.floor(viewportRef.current.width);
    const cssHeight = Math.floor(viewportRef.current.height);
    if (cssWidth <= 0 || cssHeight <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    const bitmapWidth = Math.round(cssWidth * dpr);
    const bitmapHeight = Math.round(cssHeight * dpr);
    if (canvas.width !== bitmapWidth || canvas.height !== bitmapHeight) {
      canvas.width = bitmapWidth;
      canvas.height = bitmapHeight;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
    }

    const width = cssWidth;
    const height = cssHeight;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(bitmapWidth / cssWidth, 0, 0, bitmapHeight / cssHeight, 0, 0);
    ctx.clearRect(0, 0, width, height);
    if (!scale) return;

    const { left: scrollLeft, top: scrollTop } = scrollRef.current;
    // 캔버스 좌표계: x = 시간축 x − 가로 스크롤, y = 행 top − 세로 스크롤.
    const x = (ts) => scale.x(ts) - scrollLeft;
    const info = { ctx, x, scrollLeft, scrollTop, width, height, viewLeft: 0, viewRight: width, scale, ticks };

    drawBackground?.(info);

    const currentRows = rowsRef.current;
    const { first, last } = visibleRowRange(currentRows, scrollTop, height);
    for (let i = first; i <= last; i += 1) {
      const row = currentRows[i];
      if (!row) continue;
      drawRow?.({ ...info, row, rowIndex: i, rowTop: row.top - scrollTop, rowHeight: row.height });
    }

    drawOverlay?.({ ...info, first, last });
  }, [scale, ticks, drawBackground, drawRow, drawOverlay]);

  const schedule = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      draw();
    });
  }, [draw]);

  // 병합 셀 값을 뷰포트 상단에 붙여 둔다(old TreeGrid과 같은 동작) — 공장처럼 수십·수백 행에 걸친
  // 병합은 셀 중앙에 값을 두면 스크롤 중 화면에서 사라져 "지금 어느 공장인지"를 알 수 없다.
  // **React state를 쓰지 않는다** — 스크롤 픽셀마다 리렌더가 걸린다. 보이는 병합 셀은 몇 개뿐이라
  // DOM을 직접 훑어 translateY만 갱신한다.
  // 병합 셀 라벨의 위치·크기는 **스크롤 중에 읽지 않는다.** `offsetTop`/`offsetHeight` 조회는 강제
  // 동기 레이아웃이라, 스크롤바를 드래그하는 동안 매 이벤트마다 레이아웃 + 캔버스 전체 재도색이
  // 겹치면 메인스레드가 막혀 **드래그가 멈춘 것처럼** 보인다(2026-08-19). 값은 표시 행·병합 구간·
  // 컬럼 폭이 바뀔 때만 달라지므로 그 시점에 한 번 재서 캐시하고, 스크롤 중에는 transform만 쓴다.
  const spanMetricsRef = useRef([]);

  const measureStickySpans = useCallback(() => {
    const container = leftInnerRef.current;
    if (!container) {
      spanMetricsRef.current = [];
      return;
    }

    spanMetricsRef.current = [...container.querySelectorAll("[data-gantt-span-label]")]
      .map(label => {
        const cell = label.parentElement;
        if (!cell) return null;
        return { label, top: cell.offsetTop, room: cell.offsetHeight - label.offsetHeight - 14 };
      })
      .filter(Boolean);
  }, []);

  const syncStickySpans = useCallback((scrollTop) => {
    spanMetricsRef.current.forEach(({ label, top, room }) => {
      const offset = Math.max(0, Math.min(scrollTop - top, room));
      label.style.transform = offset > 0 ? `translateY(${offset}px)` : "none";
    });
  }, []);

  // 스크롤은 **동기로** 반영한다 — 헤더/좌측 pane은 transform, 캔버스는 즉시 draw.
  // rAF로 미루면 레이어끼리 한 프레임 어긋나 보인다.
  /**
   * 보이는 헤더 눈금 범위를 갱신한다(§7-35).
   *
   * ⚠️ **가로 위치를 바꾸는 모든 경로에서 불려야 한다.** 처음에 `syncScroll` 에만 넣었더니
   * **가로 스크롤이 `syncScroll` 을 안 거쳐서**(그쪽은 세로 스크롤·리사이즈 경로다) 컨테이너
   * `transform` 만 즉시 움직이고 눈금은 옛 인덱스에 남아 **헤더가 통째로 비어 보였다**
   * (2026-09-10 신고). 세로 pane 이 `syncScroll` 한 곳만으로 되는 것과 달리, 가로는
   * `setScrollLeft`(휠·패닝·스크롤바·자동스크롤·`scrollTo`)와 `syncScroll`(크기 변경·눈금 변경)
   * **두 입구**가 있다.
   *
   * 버퍼는 뷰포트 한 폭 — 0이면 1px 스크롤마다 범위가 바뀌어 재렌더가 폭주하고,
   * 너무 크면 한 번에 그리는 칸이 늘어난다.
   */
  const syncTickRange = useCallback((left) => {
    const viewW = viewportRef.current.width;
    const buffer = Math.max(TICK_BUFFER_MIN, viewW);
    const topNext = visibleTickRange(ticks.top, left, viewW, buffer);
    const botNext = visibleTickRange(ticks.bottom, left, viewW, buffer);
    setTickRange(prev => (
      prev.topFirst === topNext.first && prev.topLast === topNext.last
        && prev.botFirst === botNext.first && prev.botLast === botNext.last
        ? prev
        : { topFirst: topNext.first, topLast: topNext.last, botFirst: botNext.first, botLast: botNext.last }
    ));
  }, [ticks]);

  const syncScroll = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // 가로 위치는 **우리가 소유**하고(scrollRef.left), 세로는 네이티브 스크롤러가 소유한다.
    // **정수로 스냅한다.** 네이티브 `scrollTop`은 고정밀 스크롤·화면 배율에서 소수가 되고(예: 123.5),
    // 그 값으로 `translateY()`를 걸면 좌측 pane·헤더 글자가 반픽셀에 놓여 흐려진다. 캔버스도 같은
    // 값으로 행 y를 계산하므로 바·글자가 함께 흐려진다. 최대 0.5px 차이는 눈에 보이지 않는다.
    const max = Math.max(0, timelineWidth - viewportRef.current.width);
    const left = Math.round(Math.min(scrollRef.current.left, max));
    const top = Math.round(scroller.scrollTop);
    scrollRef.current = { left, top };

    if (headerInnerRef.current) headerInnerRef.current.style.transform = `translateX(${-left}px)`;
    if (leftInnerRef.current) leftInnerRef.current.style.transform = `translateY(${-top}px)`;
    if (hScrollRef.current && Math.round(hScrollRef.current.scrollLeft) !== Math.round(left)) {
      hScrollRef.current.scrollLeft = left;
    }

    // 보이는 행 범위가 바뀔 때만 좌측 pane을 다시 렌더한다(스크롤 픽셀마다 리렌더 방지).
    const next = visibleRowRange(rowsRef.current, top, viewportRef.current.height);
    setRange(prev => (prev.first === next.first && prev.last === next.last ? prev : next));

    // 가로 헤더 눈금 — 크기 변경·눈금 변경(줌·버킷) 때 여기서 잡는다. 스크롤로 인한 갱신은
    // `setScrollLeft` 가 한다(위 `syncTickRange` 주석).
    syncTickRange(left);

    syncStickySpans(top);
    draw();
  }, [draw, syncStickySpans, syncTickRange, timelineWidth]);

  // 가로 이동의 **단일 창구** — 휠·Shift+휠·패닝·외부 API가 모두 여기로 들어온다.
  // `fromBar`는 가로 스크롤바가 스스로 움직여서 들어온 경우로, 되먹임(스크롤바에 다시 쓰기)을 막는다.
  const setScrollLeft = useCallback((next, fromBar = false) => {
    const max = Math.max(0, timelineWidth - viewportRef.current.width);
    const clamped = Math.round(Math.max(0, Math.min(next, max)));   // 정수 스냅(위 주석 참고)
    if (clamped === scrollRef.current.left) return;

    scrollRef.current = { ...scrollRef.current, left: clamped };
    if (headerInnerRef.current) headerInnerRef.current.style.transform = `translateX(${-clamped}px)`;
    if (!fromBar && hScrollRef.current) hScrollRef.current.scrollLeft = clamped;
    syncTickRange(clamped);            // ⚠️ 캔버스(`draw`)와 **같은 자리에서** 헤더 범위도 갱신한다
    draw();
  }, [draw, syncTickRange, timelineWidth]);

  const handleHBarScroll = (event) => setScrollLeft(event.currentTarget.scrollLeft, true);

  // ── 크기 추적 ─────────────────────────────────────────────────────────────
  useLayoutEffect(() => {
    const outer = outerRef.current;
    if (!outer) return undefined;

    const measure = () => {
      const rect = outer.getBoundingClientRect();

      // 네이티브 세로 스크롤바 두께 = 스크롤러의 (전체 폭 − 콘텐츠 폭). 자체 가로 바가 이 값을 쓴다.
      const scroller = scrollerRef.current;
      const nativeSize = scroller ? scroller.offsetWidth - scroller.clientWidth : 0;
      if (nativeSize > 0) setBarSize(prev => (prev === nativeSize ? prev : nativeSize));

      // 세로 스크롤바가 실제로 생기는지로 오른쪽 여백을 정한다(`vGutter` 주석).
      // 높이는 예약 폭과 무관하므로 판정에 순환이 없다.
      const height = Math.max(0, rect.height - TIMELINE_HEADER_HEIGHT - barSize);
      const gutter = totalHeight > height ? barSize : 0;
      setVGutter(prev => (prev === gutter ? prev : gutter));

      viewportRef.current = {
        width: Math.max(0, rect.width - leftWidth - gutter),   // ① 캔버스 폭 + 스크롤 상한
        height
      };
      syncScroll();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(outer);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
    // `barSize`가 바뀌면 뷰포트 높이를, `totalHeight`가 바뀌면 세로 여백 판정을 다시 해야 한다.
    // 둘 다 값이 달라질 때만 set하므로 루프는 없다.
  }, [leftWidth, syncScroll, barSize, totalHeight]);

  // 데이터·스케일이 바뀌면 다시 그리고 표시 범위를 재계산한다.
  useEffect(() => {
    syncScroll();
  }, [rows, totalHeight, scale, ticks, syncScroll]);

  // 좌측 pane이 다시 렌더되면(표시 행 범위·병합 구간·컬럼 폭 변경) 라벨이 새로 생기므로 **여기서만**
  // 다시 재고, 곧바로 sticky 위치를 잡아준다.
  useLayoutEffect(() => {
    measureStickySpans();
    syncStickySpans(scrollRef.current.top);
  }, [range, spanGroups, cols, measureStickySpans, syncStickySpans]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // ── 좌표 → 행/시각 ────────────────────────────────────────────────────────
  const resolvePoint = useCallback((clientX, clientY) => {
    const outer = outerRef.current;
    if (!outer) return null;

    const rect = outer.getBoundingClientRect();
    const localX = clientX - rect.left;
    const localY = clientY - rect.top;
    if (localY < TIMELINE_HEADER_HEIGHT) return null;

    const { top: scrollTop, left: scrollLeft } = scrollRef.current;
    const rowIndex = findRowIndexAtY(rowsRef.current, localY - TIMELINE_HEADER_HEIGHT + scrollTop);
    const row = rowIndex === -1 ? null : rowsRef.current[rowIndex];
    const inLeftPane = localX < leftWidth;
    const ts = scale && !inLeftPane ? scale.invert(localX - leftWidth + scrollLeft) : null;
    // `ts` 의 좌측 pane 판정을 **뺀** 값. pane 위에서는 시간축 왼쪽 바깥이라 축 시작보다 이른
    // 시각(= 외삽)이 나온다. 드래그 중에만 쓴다 — `ts`/hover 계약은 그대로 둔다(아래 `report` 주석).
    const axisTs = scale ? scale.invert(localX - leftWidth + scrollLeft) : null;

    // 좌측 pane에서 클릭한 **컬럼**까지 돌려준다 — 이게 없으면 클릭이 행만 잡고 셀 선택은 언제나
    // 첫 컬럼에 머문다(2026-08-19 확인).
    let colKey = null;
    if (inLeftPane) {
      const index = colOffsets.findIndex((left, i) => localX >= left && localX < left + cols[i].width);
      colKey = index === -1 ? null : cols[index].key;
    }

    return { row, rowIndex, inLeftPane, localX, localY, ts, axisTs, colKey };
  }, [leftWidth, scale, cols, colOffsets]);

  // 트리 컬럼의 ▶ 영역을 눌렀는지 — 좌측 pane이 입력을 받지 않으니 좌표로 판정한다.
  const isChevronHit = useCallback((localX, row) => {
    if (!row?.hasChildren) return false;

    let offset = 0;
    for (const column of cols) {
      if (column.tree) {
        // 셀 좌측 padding(8px) + 들여쓰기만큼 지나야 표식이 시작한다. 들여쓰기는 **좌측 pane과 같은
        // 함수**로 구한다 — 컬럼을 좁히면 들여쓰기에 상한이 걸리므로 계산을 복제하면 어긋난다.
        // 히트 영역이 22px 정사각이라 여유는 2px만 둔다(더 주면 옆 글자 클릭까지 삼킨다).
        const start = offset + 8 + treeIndentWidth(column, row);
        return localX >= start - 2 && localX <= start + LEFT_PANE_CHEVRON_WIDTH + 2;
      }
      offset += column.width;
    }
    return false;
  }, [cols]);

  // ── 드래그 패닝 — **스크롤바에 의존하지 않는 이동 경로** ─────────────────────────────
  // Alt+드래그 또는 가운데 버튼 드래그로 타임라인을 잡아끈다(dhtmlx/Bryntum 간트와 같은 관례).
  // 가로 스크롤바 thumb 드래그가 환경에 따라 먹지 않는 문제(§7-2k, 원인 조사 중)와 무관하게
  // 동작하는 대체 조작이며, 원인이 해결된 뒤에도 유용해서 남긴다.
  const panRef = useRef(null);

  // ── 편집 드래그 ───────────────────────────────────────────────────────────
  // 패닝·컬럼 폭 조절과 **같은 패턴**이다(window 리스너 + ref 상태). React state를 쓰지 않는 이유는
  // 여기가 mousemove마다 도는 경로여서다 — setState하면 리렌더 + 툴팁 조회가 붙어 드래그가 끊긴다
  // (§7-2k에서 가로 스크롤바 thumb가 같은 이유로 안 먹혔다).
  const dragRef = useRef(null);
  // 드래그로 끝난 mouseup 뒤에 따라오는 click을 선택으로 처리하지 않기 위한 1회용 플래그.
  const suppressClickRef = useRef(false);
  const autoScrollRef = useRef(0);

  // 커서가 뷰포트 가장자리에 오면 타임라인/행을 밀어 준다 — 화면 밖 자원으로 옮기려면 필수다.
  // 스크롤한 뒤에는 **마지막 커서 좌표로 다시 판정**해야 고스트가 따라온다(커서는 그대로인데
  // 그 아래 내용이 움직였으므로).
  const stopAutoScroll = useCallback(() => {
    cancelAnimationFrame(autoScrollRef.current);
    autoScrollRef.current = 0;
  }, []);

  useEffect(() => stopAutoScroll, [stopAutoScroll]);

  const handleMouseDown = (event) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // 좌클릭(Alt 없음) 또는 우클릭 = 편집 드래그 후보. 화면이 "잡을 게 있다"고 답할 때만 시작한다.
    // 두 버튼에 각각 무슨 뜻을 줄지는 화면 몫이다(프레임은 `button`을 전달만 한다).
    if ((event.button === 0 || event.button === 2) && !event.altKey && onBoxDragStart) {
      const point = resolvePoint(event.clientX, event.clientY);
      if (point?.row && !point.inLeftPane
        && onBoxDragStart(point.row, {
          ts: point.ts, clientX: event.clientX, clientY: event.clientY, button: event.button
        })) {
        event.preventDefault();                 // 텍스트 선택·포커스 이동 차단
        const button = event.button;
        dragRef.current = { moved: false, clientX: event.clientX, clientY: event.clientY };
        scroller.style.cursor = "grabbing";

        // ⚠️ 드래그에는 `axisTs` 를 **같이** 넘긴다. `ts` 는 좌측 pane 위에서 `null` 인데, 그걸
        // "조작 취소"로 읽으면 **왼쪽 끝까지 끈 상태에서 커서가 1px만 pane 에 들어가도 튕긴다**
        // (2026-09-10 신고). 리사이즈·슬라이드는 행과 무관한 조작이라 시간축 좌표만 있으면 되고,
        // 이동은 대상 행을 고르는 조작이라 취소가 맞다 — **그 판단은 화면 몫**이므로 둘 다 준다.
        // `ts` 의 뜻과 `onHoverPoint` 계약은 바꾸지 않는다(주문·자원 간트가 그대로 쓴다).
        const report = (clientX, clientY) => {
          const at = resolvePoint(clientX, clientY);
          onBoxDragMove?.(at?.row ?? null, {
            ts: at?.ts ?? null,
            axisTs: at?.axisTs ?? null,
            clientX,
            clientY,
            inLeftPane: Boolean(at?.inLeftPane)
          });
        };

        const step = () => {
          const drag = dragRef.current;
          if (!drag) return;

          // ⚠️ **기준은 `scroller` 지 `outer` 가 아니다.** 스크롤 레이어는 `left:0` 이라 **좌측
          // pane 을 포함**하고, `top:TIMELINE_HEADER_HEIGHT` 라 **헤더는 이미 빠져 있다**.
          // 여기를 outer 기준으로 착각해 두 축 모두 어긋나 있었다(2026-09-10):
          //   가로 — 왼쪽 띠가 pane 안쪽(컴포넌트 맨 왼쪽 36px)에 생겨, **보이는 시작 구간을 넘어
          //          끌어도 스크롤이 안 걸렸다.** 자원 컬럼을 지나쳐야 겨우 걸렸다(사용자 신고).
          //   세로 — 헤더 높이를 한 번 더 더해 위쪽 띠만 64+36=100px 로 부풀어 있었다(아래는 36px).
          // 네 방향 모두 **타임라인 영역 기준 EDGE** 로 맞춘다.
          const rect = scroller.getBoundingClientRect();
          const EDGE = 36;
          const SPEED = 14;
          const timelineLeft = rect.left + leftWidth;   // 시간축이 실제로 시작하는 x
          let dx = 0;
          let dy = 0;
          if (drag.clientX < timelineLeft + EDGE) dx = -SPEED;
          else if (drag.clientX > rect.left + scroller.clientWidth - EDGE) dx = SPEED;
          if (drag.clientY < rect.top + EDGE) dy = -SPEED;
          else if (drag.clientY > rect.top + scroller.clientHeight - EDGE) dy = SPEED;

          if (dx === 0 && dy === 0) {
            autoScrollRef.current = 0;
            return;
          }
          if (dx !== 0) setScrollLeft(scrollRef.current.left + dx);
          if (dy !== 0) scroller.scrollTop += dy;
          report(drag.clientX, drag.clientY);
          autoScrollRef.current = requestAnimationFrame(step);
        };

        const onMove = (moveEvent) => {
          const drag = dragRef.current;
          if (!drag) return;
          drag.moved = true;
          drag.clientX = moveEvent.clientX;
          drag.clientY = moveEvent.clientY;
          report(moveEvent.clientX, moveEvent.clientY);
          if (!autoScrollRef.current) autoScrollRef.current = requestAnimationFrame(step);
        };

        const detach = () => {
          stopAutoScroll();
          window.removeEventListener("mousemove", onMove, true);
          window.removeEventListener("mouseup", onUp, true);
          window.removeEventListener("keydown", onKey, true);
          scroller.style.cursor = "default";
        };

        const onUp = (upEvent) => {
          const drag = dragRef.current;
          dragRef.current = null;
          detach();
          // ⚠️ **좌클릭 드래그에만 건다.** 우클릭 뒤에는 `click`이 오지 않으므로 여기서 켜 두면
          // 플래그가 남아 **다음 좌클릭이 통째로 삼켜진다**(선택이 안 되는 것처럼 보인다).
          suppressClickRef.current = button === 0 && Boolean(drag?.moved);

          const at = resolvePoint(upEvent.clientX, upEvent.clientY);
          onBoxDragEnd?.(at?.row ?? null, {
            ts: at?.ts ?? null, clientX: upEvent.clientX, clientY: upEvent.clientY, moved: Boolean(drag?.moved)
          });
        };

        // Esc 취소 — 놓을 곳이 없어 갇히는 상황(전 행이 금지)에서 빠져나갈 유일한 경로다.
        const onKey = (keyEvent) => {
          if (keyEvent.key !== "Escape") return;
          dragRef.current = null;
          detach();
          suppressClickRef.current = button === 0;   // 위 onUp과 같은 이유
          onBoxDragCancel?.();
        };

        window.addEventListener("mousemove", onMove, true);
        window.addEventListener("mouseup", onUp, true);
        window.addEventListener("keydown", onKey, true);
        return;
      }
    }

    if (event.button !== 1 && !(event.button === 0 && event.altKey)) return;

    event.preventDefault();                     // 가운데 버튼의 브라우저 autoscroll 차단
    panRef.current = {
      x: event.clientX, y: event.clientY,
      left: scroller.scrollLeft, top: scroller.scrollTop, moved: false
    };
    scroller.style.cursor = "grabbing";

    const onMove = (moveEvent) => {
      const pan = panRef.current;
      if (!pan) return;
      pan.moved = true;
      setScrollLeft(pan.left - (moveEvent.clientX - pan.x));
      scroller.scrollTop = pan.top - (moveEvent.clientY - pan.y);
    };
    const onUp = () => {
      panRef.current = null;
      scroller.style.cursor = "default";
      window.removeEventListener("mousemove", onMove, true);
      window.removeEventListener("mouseup", onUp, true);
    };

    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("mouseup", onUp, true);
  };

  const handleClick = (event) => {
    if (event.altKey) return;                   // 패닝 제스처의 마무리 클릭은 선택이 아니다
    // 편집 드래그로 끝난 mouseup 뒤에도 click이 따라온다 — 그걸 선택으로 처리하면 놓은 자리의
    // 다른 바가 선택돼 버린다(패닝의 `altKey` 가드와 같은 역할).
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    const point = resolvePoint(event.clientX, event.clientY);
    if (!point?.row) return;

    // 좌측 pane을 클릭하면 **그 셀**이 선택된다(포커스 링). 타임라인 클릭은 열 개념이 없어 직전 열을 유지.
    setFocus({
      rowKey: point.row.rowKey,
      colKey: point.colKey ?? focus.colKey ?? cols[0]?.key ?? null
    });

    if (point.inLeftPane) {
      if (isChevronHit(point.localX, point.row)) onToggleRow?.(point.row);
      return;
    }
    onRowActivate?.(point.row, { ts: point.ts, clientX: event.clientX, clientY: event.clientY });
  };

  // 우클릭 — 브라우저 기본 메뉴를 막고 그 지점의 행·시각을 화면에 넘긴다. 어느 바인지는 화면이
  // `ts`로 판정한다(프레임은 박스를 모른다 — 좌표 소유권은 화면에 있다).
  // 좌측 pane에서는 열지 않는다(old도 간트 영역 전용이었다).
  const handleContextMenu = (event) => {
    // 우클릭이 제스처인 화면은 **어디서 놓든** 기본 메뉴를 막는다. 바 위에서만 막으면 드래그를
    // 빈 곳에서 놓았을 때 메뉴가 뜬다(Windows는 contextmenu가 mouseup에 온다).
    if (suppressContextMenu) {
      event.preventDefault();
      return;
    }

    const point = resolvePoint(event.clientX, event.clientY);
    if (!point?.row || point.inLeftPane) return;

    event.preventDefault();
    onContextPoint?.(point.row, { ts: point.ts, clientX: event.clientX, clientY: event.clientY });
  };

  const handleDoubleClick = (event) => {
    const point = resolvePoint(event.clientX, event.clientY);
    if (!point?.row) return;

    if (point.inLeftPane) {
      onToggleRow?.(point.row);
      return;
    }
    onRowDoubleActivate?.(point.row, { ts: point.ts, clientX: event.clientX, clientY: event.clientY });
  };

  const handleMouseMove = (event) => {
    const scrollerEl = scrollerRef.current;

    // ⚠️ **버튼을 누르고 있는 동안은 아무것도 하지 않는다.**
    // 가로 스크롤바 thumb를 드래그하는 중에도 이 레이어로 mousemove가 계속 들어온다. 그때 hover
    // 상태를 갱신하면 화면이 리렌더되고(툴팁 조회까지 붙는다) **브라우저의 thumb 드래그가 끊긴다**
    // — 트랙 클릭(mousedown 1회)은 되는데 드래그만 안 되던 증상의 원인이다(2026-08-19 사용자 제보).
    // 이후 드래그 편집(AdjustmentGantt)이 들어와도 같은 가드가 필요하다.
    if (event.buttons !== 0) return;

    // 스크롤바 영역(오른쪽·아래 끝 띠) 위에서도 hover를 만들지 않는다 — 마지막 행이 계속 hover로
    // 잡혀 툴팁이 깜빡인다. clientWidth/Height는 스크롤바를 **제외한** 크기다.
    if (scrollerEl) {
      const rect = scrollerEl.getBoundingClientRect();
      const overScrollbar = event.clientX - rect.left >= scrollerEl.clientWidth
        || event.clientY - rect.top >= scrollerEl.clientHeight;
      if (overScrollbar) {
        setChevronHoverKey(null);
        onHoverPoint?.(null, {});
        return;
      }
    }

    const point = resolvePoint(event.clientX, event.clientY);

    // 좌측 pane은 입력을 받지 않으므로(pointerEvents:none) **커서 모양도 이 레이어가 책임진다** —
    // 트리 ▶ 위에서는 손가락 커서로 "한 번 누르면 펼쳐진다"를 알린다. state로 두면 스크롤·이동마다
    // 리렌더가 걸리므로 DOM style을 직접 쓴다.
    const scroller = scrollerRef.current;
    const overChevron = Boolean(point?.inLeftPane && point.row && isChevronHit(point.localX, point.row));
    if (scroller) {
      // 편집 화면은 "잡을 수 있는 바" 위에서 `grab`을 띄워야 하는데, 무엇이 잡히는지는 화면만 안다
      // → 판정을 화면에 위임하고 프레임은 결과를 DOM style에 반영만 한다(state 없음 = 리렌더 없음).
      const custom = overChevron || !resolveCursor
        ? null
        : resolveCursor(point?.row ?? null, { ts: point?.ts ?? null, inLeftPane: Boolean(point?.inLeftPane) });
      const cursor = overChevron ? "pointer" : (custom || "default");
      if (scroller.style.cursor !== cursor) scroller.style.cursor = cursor;
    }
    const nextHover = overChevron ? point.row.rowKey : null;
    setChevronHoverKey(prev => (prev === nextHover ? prev : nextHover));

    if (!point) {
      onHoverPoint?.(null, {});
      return;
    }
    // 좌측 pane의 hover도 **행·컬럼까지 넘긴다** — 화면이 셀 툴팁(자원명 등)을 붙일 수 있어야 한다.
    // 좌측 pane은 `pointerEvents:none`이라 스스로 hover를 못 받으므로 이 레이어가 유일한 창구다.
    // 이때 `ts`는 null이고 화면의 바 판정은 전부 `ts == null`을 걸러내므로 바 툴팁과 섞이지 않는다.
    onHoverPoint?.(point.row, {
      ts: point.ts,
      clientX: event.clientX,
      clientY: event.clientY,
      inLeftPane: point.inLeftPane,
      colKey: point.colKey
    });
  };

  // 휠 — **네이티브 리스너로 직접 붙인다**(`{ passive: false }`).
  // React 17+는 wheel을 루트에 **passive**로 위임하므로 `onWheel` 안의 `preventDefault()`가 먹지 않는다
  // (Ctrl+휠이 브라우저 페이지 줌으로 새는 것도 같은 이유였다).
  //
  // 가로 입력(트랙패드 좌우 스와이프 `deltaX`, 관례상 Shift+휠)도 여기서 **직접** 처리한다:
  // 세로가 끝에 닿으면 브라우저가 제스처 대상을 상위 스크롤 컨테이너로 넘기고(scroll chaining) 그
  // 제스처가 끝날 때까지 거기에 고정돼, **맨 아래로 내린 뒤에는 가로 스크롤이 간트에 오지 않았다**
  // (2026-08-19 사용자 제보). `overscrollBehavior: "contain"`으로 전파를 끊고, 가로는 우리가 적용한다.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    const onWheel = (event) => {
      if (event.ctrlKey) {
        if (!onWheelZoom) return;
        event.preventDefault();
        const point = resolvePoint(event.clientX, event.clientY);
        onWheelZoom(event.deltaY, { ts: point?.ts ?? null });
        return;
      }

      const horizontal = event.shiftKey ? event.deltaY : event.deltaX;
      if (!horizontal) return;                    // 순수 세로 휠은 브라우저 기본 동작에 맡긴다

      event.preventDefault();
      setScrollLeft(scrollRef.current.left + horizontal);
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, [onWheelZoom, resolvePoint, setScrollLeft]);

  // ── 키보드 내비게이션 + 셀 복사 (좌측 데이터 영역 대상) ─────────────────────
  const moveFocusRow = useCallback((delta) => {
    const currentRows = rowsRef.current;
    if (!currentRows.length) return;

    const index = Math.max(0, currentRows.findIndex(row => row.rowKey === focus.rowKey));
    const next = Math.min(currentRows.length - 1, Math.max(0, index + delta));
    const row = currentRows[next];
    setFocus(prev => ({ rowKey: row.rowKey, colKey: prev.colKey ?? cols[0]?.key ?? null }));

    // 화면 밖이면 스크롤해서 보여준다.
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { height } = viewportRef.current;
    if (row.top < scroller.scrollTop) scroller.scrollTop = row.top;
    else if (row.top + row.height > scroller.scrollTop + height) scroller.scrollTop = row.top + row.height - height;
  }, [focus.rowKey, cols]);

  const moveFocusCol = useCallback((delta) => {
    setFocus(prev => {
      const index = Math.max(0, cols.findIndex(column => column.key === prev.colKey));
      const next = Math.min(cols.length - 1, Math.max(0, index + delta));
      return { ...prev, colKey: cols[next]?.key ?? prev.colKey };
    });
  }, [cols]);

  const copyFocused = useCallback((wholeRow) => {
    const row = rowsRef.current.find(item => item.rowKey === focus.rowKey);
    if (!row) return;

    const text = wholeRow
      ? cols.map(column => column.valueGetter(row) ?? "").join("\t")
      : String(cols.find(column => column.key === focus.colKey)?.valueGetter(row) ?? "");

    writeClipboard(text);
    // TODO(MIG-T2): [gantt/공통] 복사 완료 피드백(스낵바) 미이관.
    //   old: 없음(자체 구현 기능). 복사는 화면에 보이지 않는 동작이라 먹었는지 알기 어렵다
    //   사유: 쓸 수 있는 공통 랭팩 키가 없다("복사"(`copy`)는 버튼 라벨, `MSG_CopySuccess`는
    //         '활성 버전으로 복사 성공'이라 의미가 다르다). 새 키 추가 여부는 사람 판단 대기
  }, [cols, focus]);

  const handleKeyDown = (event) => {
    const row = rowsRef.current.find(item => item.rowKey === focus.rowKey);

    switch (event.key) {
      case "ArrowDown": moveFocusRow(1); break;
      case "ArrowUp": moveFocusRow(-1); break;
      case "PageDown": moveFocusRow(10); break;
      case "PageUp": moveFocusRow(-10); break;
      case "Home": moveFocusRow(-rowsRef.current.length); break;
      case "End": moveFocusRow(rowsRef.current.length); break;
      case "ArrowRight":
        if (row?.hasChildren && !expandedKeys?.has(row.rowKey)) onToggleRow?.(row);
        else moveFocusCol(1);
        break;
      case "ArrowLeft":
        if (row?.hasChildren && expandedKeys?.has(row.rowKey)) onToggleRow?.(row);
        else moveFocusCol(-1);
        break;
      case "Enter":
        if (row?.hasChildren) onToggleRow?.(row);
        break;
      case "c":
      case "C":
        if (event.ctrlKey || event.metaKey) copyFocused(event.shiftKey);
        else return;
        break;
      default:
        return;
    }
    event.preventDefault();
  };

  // ── 컬럼 폭 드래그 조절 (헤더는 스크롤 레이어 밖이라 자기 포인터 이벤트를 그대로 쓴다) ──────
  const startResize = (event, column) => {
    event.preventDefault();
    event.stopPropagation();
    resizeRef.current = { key: column.key, startX: event.clientX, startWidth: column.width };

    const onMove = (moveEvent) => {
      const drag = resizeRef.current;
      if (!drag) return;
      const next = Math.max(COL_MIN_WIDTH, Math.round(drag.startWidth + (moveEvent.clientX - drag.startX)));
      setWidthOverrides(prev => (prev[drag.key] === next ? prev : { ...prev, [drag.key]: next }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };

    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  useEffect(() => {
    if (!apiRef) return undefined;
    apiRef.current = {
      redraw: schedule,
      getScroll: () => ({ ...scrollRef.current }),
      scrollTo: ({ left, top }) => {
        const scroller = scrollerRef.current;
        if (left != null) setScrollLeft(left);
        if (top != null && scroller) scroller.scrollTop = top;
      }
    };
    return () => { apiRef.current = null; };
  }, [apiRef, schedule, setScrollLeft]);

  const visibleRows = rows.slice(range.first, range.last + 1);
  // 병합 셀은 뷰포트와 **겹치기만** 하면 그린다(시작 행이 위로 벗어나 있어도).
  const visibleSpans = spanGroups.filter(group => group.last >= range.first && group.first <= range.last);

  return (
    <Box ref={outerRef} sx={{ position: "relative", width: "100%", height: "100%", overflow: "hidden", bgcolor: "#fff" }}>
      {/* 헤더 — 좌측 컬럼 라벨 + 2단 시간축 */}
      {/* 하단 경계는 **1px 회색**이다(2026-08-21). 전에는 `2px solid UI.headerAccent`(청록)였는데
          ① 액센트색은 상호작용·상태를 말하는 색이고(포커스 링·로딩 바·검색 배지·일치 자원 레일이
          모두 이 색이다) 그걸 **항상 켜져 있는 구조선**에 쓰면 색의 의미가 닳는다 ② 앱 그리드
          (balham)의 헤더 하단이 1px 회색이라 간트만 2px 청록이면 나란히 놓였을 때 튄다.
          선을 아예 없애지는 않는다 — 헤더 배경(#fbfdfe)이 흰색에 가까워 경계가 사라지면 하단 눈금
          행과 첫 데이터 행이 붙어 보인다. */}
      {/* 헤더 밴드 — 좌측 라벨과 타임라인 눈금이 **한 Box 안**이라 둘 사이에 이음매가 생기지 않는다.
          면은 브랜드 틴트(`headerBg`), 하단 선은 무채(`headerEdge`) — 근거는 두 토큰의 주석(§7-26). */}
      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, height: TIMELINE_HEADER_HEIGHT, display: "flex", zIndex: 3, bgcolor: UI.headerBg, borderBottom: `1px solid ${UI.headerEdge}` }}>
        {/* 좌측 라벨 블록 — 폭이 캔버스 시작 지점(left: leftWidth)과 정확히 같아야 하므로 border-box */}
        {/* 경계선은 **아래 `paneDivider` 하나가 헤더~본문을 관통해** 그린다(§7-10).
            여기서 각자 그리면 헤더는 그림자만, 본문은 선만 보이는 식으로 갈렸다(2026-08-21 사용자 지적).
            투명 1px은 **폭 계산을 유지하기 위한 것**이다 — border-box라 이걸 빼면 셀 내용이 1px 밀려
            헤더와 본문 정렬이 어긋난다. */}
        <Box
          sx={{
            width: leftWidth, boxSizing: "border-box", flex: "0 0 auto", display: "flex",
            borderRight: "1px solid transparent"
          }}
        >
          {cols.map(column => (
            <Box
              key={column.key}
              sx={{
                position: "relative",
                width: column.width, boxSizing: "border-box", flex: "0 0 auto",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: UI.headerFontSize, fontWeight: UI.headerFontWeight, color: UI.headerFg,
                borderRight: `1px solid ${UI.colLine}`,
                overflow: "hidden", whiteSpace: "nowrap", px: "6px"
              }}
            >
              {column.label}

              {/* 컬럼 값 필터 트리거 — `column.filterable`인 컬럼만. **프레임은 도메인을 모른다**
                  (자원인지 공장인지). 클릭을 화면으로 올려주고, 팝오버는 화면이 띄운다.
                  좌측 pane은 `pointerEvents:none`이라 클릭을 못 받으므로 트리거는 여기(헤더)여야 한다.
                  ⚠️ `right`는 **10 이상**이어야 한다 — 아래 폭 조절 손잡이가 `right:-3 + width:7`로
                  right 4까지 덮으므로, 더 붙이면 필터를 누르려다 리사이즈가 시작된다(시안에서 확인). */}
              {column.filterable && (
                // 툴팁은 **MUI `Tooltip`**을 쓴다 — 네이티브 `title`은 브라우저 기본 말풍선(흰 배경·
                // 테두리·느린 등장)이라 툴바 아이콘들(`GanttViewControls`의 `ZoomButton`)과 모양이 갈렸다
                // (2026-08-21 사용자 지적). `span` 래퍼도 그쪽과 같은 관례다(Tooltip이 자식 ref를 잡는다).
                <Tooltip title={filterTitle}>
                  <Box
                    component="span"
                    sx={{
                      position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                      display: "flex", alignItems: "center"
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onMouseDown={(event) => event.stopPropagation()}
                      onClick={(event) => {
                        event.stopPropagation();
                        onColumnFilterClick?.(column, event.currentTarget);
                      }}
                      sx={{
                        width: 20, height: 20, p: 0, display: "flex", alignItems: "center", justifyContent: "center",
                        border: "none", borderRadius: 1, background: "transparent", cursor: "pointer",
                        color: column.filterActive ? UI.headerAccent : UI.headerFgDim,
                        "&:hover": { backgroundColor: "rgba(15, 23, 42, 0.06)" }
                      }}
                    >
                      {column.filterActive ? <FilterAltIcon sx={{ fontSize: 15 }} /> : <FilterAltOutlinedIcon sx={{ fontSize: 15 }} />}
                    </Box>
                  </Box>
                </Tooltip>
              )}

              {/* 폭 조절 손잡이 — 일반 그리드와 같은 조작(경계에 커서를 두고 드래그) */}
              <Box
                onMouseDown={(event) => startResize(event, column)}
                onDoubleClick={(event) => {
                  // 더블클릭 = 화면이 정의한 기본 폭으로 되돌리기
                  event.stopPropagation();
                  setWidthOverrides(prev => {
                    if (!(column.key in prev)) return prev;
                    const next = { ...prev };
                    delete next[column.key];
                    return next;
                  });
                }}
                sx={{
                  position: "absolute", top: 0, bottom: 0, right: -3, width: 7,
                  // hover 배경을 두지 않는다(2026-08-21 사용자 지시) — 폭 7px 띠가 헤더 높이 전체에
                  // 검정 12%로 깔려서, 컬럼 경계에 마우스가 스칠 때마다 회색 막대가 번쩍였다.
                  // 조작 가능하다는 신호는 **`col-resize` 커서**가 이미 충분히 준다(앱 그리드도 동일).
                  cursor: "col-resize", zIndex: 1
                }}
              />
            </Box>
          ))}
        </Box>
        <Box sx={{ position: "relative", flex: 1, overflow: "hidden" }}>
          <Box ref={headerInnerRef} sx={{ position: "absolute", top: 0, left: 0, height: "100%", width: timelineWidth }}>
            <TimelineHeaderTicks ticks={ticks} range={tickRange} />
          </Box>
        </Box>
        {/* ③ 세로 스크롤바 자리 — **헤더 눈금 clip 을 캔버스와 같은 폭으로** 맞춘다(`vGutter` 주석).
            헤더 밴드 자체는 `right: 0` 이라 배경·하단선은 끊기지 않고, 눈금만 여기서 멈춘다.
            이게 없으면 헤더가 캔버스보다 `barSize` 만큼 더 넓게 보여 마지막 눈금 칸이 어긋난다. */}
        {vGutter > 0 && <Box sx={{ width: vGutter, flex: "0 0 auto" }} />}
      </Box>

      {/* 좌측 데이터 pane — 렌더 전용 */}
      <Box
        sx={{
          position: "absolute", top: TIMELINE_HEADER_HEIGHT, left: 0, width: leftWidth,
          boxSizing: "border-box", bottom: barSize, zIndex: 2, pointerEvents: "none",
          // 경계선은 `paneDivider`가 그린다(위 헤더 블록과 같은 이유). 투명 1px = 폭 유지용.
          borderRight: "1px solid transparent",
          overflow: "hidden"
        }}
      >
        <Box ref={leftInnerRef} sx={{ position: "absolute", top: 0, left: 0, right: 0, height: totalHeight }}>
          <GanttLeftPane
            rows={visibleRows}
            columns={cols}
            spannedKeys={spannedKeys}
            spans={visibleSpans}
            expandedKeys={expandedKeys}
            focusedRowKey={focus.rowKey}
            focusedColKey={focus.colKey}
            selectedRowKey={selectedRowKey}
            chevronHoverKey={chevronHoverKey}
          />
        </Box>
      </Box>

      {/* 타임라인 캔버스 — 뷰포트 크기 */}
      <canvas ref={canvasRef} style={{ position: "absolute", top: TIMELINE_HEADER_HEIGHT, left: leftWidth, zIndex: 1 }} />

      {/* 스크롤·입력 레이어 — 투명, 최상단 */}
      <Box
        ref={scrollerRef}
        tabIndex={0}
        onScroll={syncScroll}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onDoubleClick={handleDoubleClick}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          if (scrollerRef.current) scrollerRef.current.style.cursor = "default";
          setChevronHoverKey(null);
          onHoverPoint?.(null, {});
        }}
        onKeyDown={handleKeyDown}
        sx={{
          position: "absolute", top: TIMELINE_HEADER_HEIGHT, left: 0, right: 0, bottom: barSize,
          // 가로는 **자체 스크롤바**가 소유한다(아래 참고) → 네이티브 가로 스크롤은 끈다.
          overflowX: "hidden", overflowY: "auto", zIndex: 4, outline: "none",
          // 세로가 끝에 닿아도 제스처를 상위로 넘기지 않는다 — 넘기면 그 제스처 동안 가로 입력까지
          // 상위 컨테이너로 가서 간트가 멈춘 것처럼 보인다.
          overscrollBehavior: "contain"
        }}
      >
        <Box sx={{ width: "100%", height: Math.max(1, totalHeight) }} />
      </Box>

      {/* 가로 스크롤바(자체) — 타임라인 영역 아래. 트랙 클릭/thumb 드래그 모두 위 handleHBarDown이
          처리하고, 이동은 window 캡처 리스너로 받는다(네이티브 스크롤바 동작에 의존하지 않는다). */}
      {/* 가로 스크롤바 — **네이티브**. 얇은 전용 스크롤 영역에 타임라인 전체 폭짜리 스페이서를 두면
          브라우저가 세로와 완전히 같은 스크롤바를 그려준다(색·화살표·두께·hover 전부). 위치 소유권은
          우리에게 있고, 여기서는 `scroll` 이벤트만 받아 반영한다. */}
      <Box
        ref={hScrollRef}
        onScroll={handleHBarScroll}
        sx={{
          // ② `right: vGutter` — 세로 스크롤바 자리를 피한다. **자기 스크롤 범위가 캔버스와 같아야**
          //    한다: 스페이서가 `timelineWidth` 라 최대 스크롤이 `timelineWidth - 자기 clientWidth`로
          //    정해지는데, 여기를 `right: 0` 으로 두면 `setScrollLeft` 의 상한만 커지고
          //    **그 값에 도달할 입력 수단이 없다**(축 끝이 여전히 안 보인다). `vGutter` 주석 참고.
          position: "absolute", left: leftWidth, right: vGutter, bottom: 0, height: barSize, zIndex: 6,
          overflowX: "auto", overflowY: "hidden"
        }}
      >
        <Box sx={{ width: `${timelineWidth}px`, height: "1px" }} />
      </Box>

      {/* 고정 영역 ↔ 타임라인 경계 — **이 하나가 헤더~본문을 관통해** 그린다.
          이전에는 헤더 블록과 좌측 pane이 각자 `borderRight` + `boxShadow`를 갖고 있었는데, 두 요소가
          서로 다른 배경(헤더 `headerBg` / 본문 흰 캔버스) 위에 있고 이웃 요소와의 겹침 순서도 달라
          **헤더는 그림자처럼, 본문은 진한 선처럼** 보였다(2026-08-21 사용자 지적). 한 요소가 그리면
          그 차이가 구조적으로 사라진다.
          `pointerEvents: "none"`이라 위에 있어도 스크롤·클릭은 그대로 아래 레이어로 간다.
          아래로는 가로 스크롤바 위까지만 내려온다(스크롤바를 가로지르면 부품처럼 보인다). */}
      <Box
        sx={{
          position: "absolute", top: 0, bottom: barSize, left: leftWidth - 1, width: "1px",
          bgcolor: UI.paneSepStrong, boxShadow: UI.paneSepShadow,
          zIndex: 5, pointerEvents: "none"
        }}
      />

      {/* 조회 중 — 진행 바 + 옅은 베일. **화면 전체 Backdrop이 아니라 간트 영역 안**이다:
          FP Report 화면들이 쓰는 AG Grid `loading` 오버레이와 같은 범위이고(그리드 안쪽만 덮는다),
          조회조건 영역은 계속 조작할 수 있어야 한다. 베일이 스크롤 레이어보다 위에 있어 로딩 중
          클릭·스크롤이 막히는 것도 그리드 동작과 같다. */}
      {loading && (
        <Box sx={{ position: "absolute", top: TIMELINE_HEADER_HEIGHT, left: 0, right: 0, bottom: 0, zIndex: 5 }}>
          <LinearProgress
            sx={{
              height: "3px",
              bgcolor: "transparent",
              "& .MuiLinearProgress-bar": { bgcolor: UI.headerAccent }
            }}
          />
          <Box
            sx={{
              position: "absolute", inset: 0, bgcolor: "rgba(255, 255, 255, 0.55)",
              display: "flex", alignItems: "center", justifyContent: "center"
            }}
          >
            {/* **메시지 없는 베일은 "멈춘 화면"으로 읽힌다**(2026-08-19 사용자 지적). 진행 바만으로는
                시선이 상단 3px에만 걸려 본문이 정지한 것처럼 보인다. 그래서 뷰포트 가운데에 스피너 +
                문구 카드를 얹는다 — 문구는 이미 6개 언어에 있는 공통 키 `MSG_Loading`을 쓴다
                (랭팩에 없는 키를 새로 만들지 않는다는 §0-3 규칙). */}
            <Box
              sx={{
                display: "flex", alignItems: "center", gap: 1.25,
                px: 2, py: 1.25, borderRadius: "8px",
                bgcolor: "#fff", border: `1px solid ${UI.paneSep}`,
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.10)"
              }}
            >
              <CircularProgress size={18} thickness={4.5} sx={{ color: UI.headerAccent }} />
              <Typography variant="caption" sx={{ color: UI.cellFg, whiteSpace: "nowrap" }}>
                {loadingMessage}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GanttFrame;
